/**
 * Upload stage beacon: what happened on the phone between picking a file and
 * the upload finishing, reported even when the upload never reaches the
 * server at all.
 *
 * Why (2026-10-19, Bobbie, a brand-new iPhone photo in Messages, about
 * 2:50pm Central): the wait "crawled", and the Netlify logs held no reserve
 * call from her phone at all, so every server log we had was silent about
 * the one attempt that mattered. The steps before reserve (the file picker,
 * the photo shrink, the shrink queue) ran with no trace anywhere. This sends
 * one small line per attempt to /.netlify/functions/uploadStage, which only
 * writes it to the function log, so the next slow attempt tells the truth.
 *
 * PRIVACY, read before adding a field: timings, a kind (image, video, file),
 * a MIME type from a fixed list, a size BUCKET, how the shrink ended, and an
 * error code. No file name, no id, no uid, no thread, no target. Nothing is
 * stored anywhere except the function's own log line.
 *
 * Sent with navigator.sendBeacon, so a line still leaves when the person
 * closes the app or switches away mid-wait (that is exactly the attempt we
 * are missing). An attempt still open when the page is hidden sends one
 * interim line with outcome 'open' and the stage it reached; its final line
 * follows if it later settles.
 *
 * Imports nothing, so it is pinned by a plain Node test
 * (tests/upload-stage-beacon.test.mjs).
 */

export const UPLOAD_STAGE_ENDPOINT = '/.netlify/functions/uploadStage';

export type ShrinkResult = 'ok' | 'skipped' | 'original' | 'timeout' | 'refused' | 'error';
export type ShrinkPath = 'worker' | 'main' | 'none';
export type UploadStageOutcome =
  | 'sent'
  | 'failed'
  | 'open'
  | 'shrink-timeout'
  | 'shrink-refused'
  | 'shrink-error';

const KNOWN_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
  'video/mp4', 'video/quicktime', 'video/webm',
  'application/pdf', 'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/wav', 'text/plain', 'text/csv',
]);

/** Size buckets in MB, upper bounds, so the line never carries an exact byte count. */
const SIZE_BUCKETS_MB: Array<[number, string]> = [
  [0.5, '<0.5MB'], [1, '0.5-1MB'], [2, '1-2MB'], [4, '2-4MB'], [8, '4-8MB'],
  [25, '8-25MB'], [100, '25-100MB'], [1024, '100MB-1GB'],
];

export function uploadSizeBucket(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return 'unknown';
  const mb = bytes / (1024 * 1024);
  for (const [limit, label] of SIZE_BUCKETS_MB) if (mb < limit) return label;
  return '>1GB';
}

function typeLabel(type: string): string {
  const clean = (type || '').toLowerCase();
  return KNOWN_TYPES.has(clean) ? clean : clean ? 'other' : 'blank';
}

function kindOf(type: string): 'image' | 'video' | 'file' {
  const clean = (type || '').toLowerCase();
  if (clean.startsWith('image/')) return 'image';
  if (clean.startsWith('video/')) return 'video';
  return 'file';
}

interface Stage {
  type: string;
  size: number;
  pickedAt: number | null;
  createdAt: number;
  shrinkStartAt: number | null;
  shrinkMs: number | null;
  shrinkResult: ShrinkResult | null;
  shrinkPath: ShrinkPath | null;
  sentSize: number | null;
  reserveStartAt: number | null;
  interimSent: boolean;
  finished: boolean;
  /** 1 for the first try of this file, 2 for its first retry, and so on. */
  attempt: number;
}

export interface UploadStageDeps {
  now: () => number;
  send: (body: string) => void;
  standalone: () => boolean;
}

export interface UploadStagePayload {
  v: 1;
  kind: 'image' | 'video' | 'file';
  type: string;
  size: string;
  sentSize: string | null;
  standalone: boolean;
  outcome: UploadStageOutcome;
  attempt: number;
  stage: 'picked' | 'shrinking' | 'shrunk' | 'reserving';
  code: string | null;
  pickToShrinkMs: number | null;
  shrinkMs: number | null;
  shrinkResult: ShrinkResult | null;
  shrinkPath: ShrinkPath | null;
  pickToReserveMs: number | null;
  totalMs: number;
}

function cleanCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return /^[a-z][a-z0-9_]{0,47}$/.test(value) ? value : 'other';
}

function ms(value: number | null): number | null {
  return value === null ? null : Math.max(0, Math.round(value));
}

export function createUploadStageTracker(deps: UploadStageDeps) {
  const byBlob = new WeakMap<Blob, Stage>();
  const open = new Set<Stage>();

  const freshStage = (blob: Blob, pickedAt: number | null, attempt: number): Stage => ({
    type: blob.type || '',
    size: blob.size,
    pickedAt,
    createdAt: deps.now(),
    shrinkStartAt: null,
    shrinkMs: null,
    shrinkResult: null,
    shrinkPath: null,
    sentSize: null,
    reserveStartAt: null,
    interimSent: false,
    finished: false,
    attempt,
  });

  /**
   * A start event (shrink or reserve) on an attempt that already settled is
   * a RETRY of the same draft (refute round 1 on #1401: the first settle used
   * to mute every later try). It gets its own stage and its own lines.
   */
  const beginFor = (blob: Blob): Stage => {
    const existing = byBlob.get(blob);
    if (existing && existing.finished) {
      const next = freshStage(blob, existing.pickedAt, Math.min(existing.attempt + 1, 99));
      byBlob.set(blob, next);
      return next;
    }
    return stageFor(blob);
  };

  const stageFor = (blob: Blob): Stage => {
    let stage = byBlob.get(blob);
    if (!stage) {
      stage = {
        type: blob.type || '',
        size: blob.size,
        pickedAt: null,
        createdAt: deps.now(),
        shrinkStartAt: null,
        shrinkMs: null,
        shrinkResult: null,
        shrinkPath: null,
        sentSize: null,
        reserveStartAt: null,
        interimSent: false,
        finished: false,
        attempt: 1,
      };
      byBlob.set(blob, stage);
    }
    return stage;
  };

  const payload = (stage: Stage, outcome: UploadStageOutcome, code: unknown): UploadStagePayload => {
    const start = stage.pickedAt ?? stage.createdAt;
    const now = deps.now();
    const stageName = stage.reserveStartAt !== null
      ? 'reserving'
      : stage.shrinkResult !== null
        ? 'shrunk'
        : stage.shrinkStartAt !== null ? 'shrinking' : 'picked';
    return {
      v: 1,
      kind: kindOf(stage.type),
      type: typeLabel(stage.type),
      size: uploadSizeBucket(stage.size),
      sentSize: stage.sentSize === null ? null : uploadSizeBucket(stage.sentSize),
      standalone: deps.standalone(),
      outcome,
      attempt: stage.attempt,
      stage: stageName,
      code: cleanCode(code),
      pickToShrinkMs: stage.shrinkStartAt === null ? null : ms(stage.shrinkStartAt - start),
      shrinkMs: stage.shrinkMs !== null
        ? ms(stage.shrinkMs)
        : stage.shrinkStartAt !== null ? ms(now - stage.shrinkStartAt) : null,
      shrinkResult: stage.shrinkResult,
      shrinkPath: stage.shrinkPath,
      pickToReserveMs: stage.reserveStartAt === null ? null : ms(stage.reserveStartAt - start),
      totalMs: ms(now - start) ?? 0,
    };
  };

  const emit = (stage: Stage, outcome: UploadStageOutcome, code?: unknown) => {
    try {
      deps.send(JSON.stringify(payload(stage, outcome, code)));
    } catch {
      // A beacon must never break an upload.
    }
  };

  return {
    /** The person picked this file. Starts the clock. */
    picked(file: Blob): void {
      const stage = stageFor(file);
      if (stage.pickedAt === null) stage.pickedAt = deps.now();
    },
    shrinkStarted(file: Blob): void {
      const stage = beginFor(file);
      stage.shrinkStartAt = deps.now();
      // Open from here: a picked draft that was never sent is not an attempt.
      if (!stage.finished) open.add(stage);
    },
    /** The shrink ended; `output` (when it is a new blob) carries the same attempt on to reserve. */
    shrinkEnded(file: Blob, result: ShrinkResult, path: ShrinkPath, output?: Blob | null): void {
      const stage = stageFor(file);
      const now = deps.now();
      stage.shrinkMs = stage.shrinkStartAt === null ? null : now - stage.shrinkStartAt;
      stage.shrinkResult = result;
      stage.shrinkPath = path;
      if (output) {
        stage.sentSize = output.size;
        if (output !== file) byBlob.set(output, stage);
      }
      // The shrink is over, so the attempt is no longer stuck in it. It is
      // open again only if it reaches reserveStarted (refute round 2 on
      // #1401): class photos, avatars, post images and the direct thread
      // path upload without the gateway, and a refusal after a good shrink
      // never reaches it either, so leaving the attempt open here reported
      // every one of them as a phantom 'open' at stage 'shrunk' on the next
      // pagehide.
      open.delete(stage);
      if (result === 'timeout' && output) return; // goes on to reserve with the original
      if (result === 'timeout' || result === 'refused' || result === 'error') {
        if (!output) {
          stage.finished = true;
          open.delete(stage);
          emit(stage, result === 'timeout' ? 'shrink-timeout' : result === 'refused' ? 'shrink-refused' : 'shrink-error');
        }
      }
    },
    reserveStarted(blob: Blob): void {
      const stage = beginFor(blob);
      stage.reserveStartAt = deps.now();
      if (!stage.finished) open.add(stage);
      if (stage.sentSize === null) stage.sentSize = blob.size;
    },
    /** The upload settled. One final line. */
    settled(blob: Blob, outcome: 'sent' | 'failed', code?: unknown): void {
      const stage = stageFor(blob);
      if (stage.finished) return;
      stage.finished = true;
      open.delete(stage);
      emit(stage, outcome, code);
    },
    /** The page is being hidden or closed: one interim line per attempt still open. */
    flushOpen(): void {
      for (const stage of open) {
        if (stage.interimSent || stage.finished) continue;
        stage.interimSent = true;
        emit(stage, 'open');
      }
    },
    /** Test seam. */
    openCount(): number {
      return open.size;
    },
  };
}

function browserSend(body: string): void {
  // Only a real page reports, and only through sendBeacon (every browser the
  // Hub supports has it). No fetch fallback on purpose: a beacon must never
  // show up as one more request in an upload's own request count, which is
  // exactly what the upload tests (and the #1392 transfer tally) count.
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
  if (typeof navigator.sendBeacon !== 'function') return;
  const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
  navigator.sendBeacon(UPLOAD_STAGE_ENDPOINT, blob);
}

function browserStandalone(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const nav = navigator as Navigator & { standalone?: boolean };
    return nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true;
  } catch {
    return false;
  }
}

export const uploadStage = createUploadStageTracker({
  now: () => Date.now(),
  send: browserSend,
  standalone: browserStandalone,
});

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function'
    && typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  const flush = () => uploadStage.flushOpen();
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
